// Day-disable resolution. Kept separate from the calendar so the rules are
// unit-testable in isolation and shared by keyboard navigation (which must skip
// disabled days) and rendering (which must style them).

import type { Civil } from './date-utils';
import { civilFromInstant, compareDate, daysInMonth, isSameDay, weekday } from './date-utils';
import type { DatePickerBaseProps } from './types';

export interface DisableContext {
  timezone: string;
  now: Date;
  minDate?: Date;
  maxDate?: Date;
  disableFuture?: boolean;
  disablePast?: boolean;
  disableWeekends?: boolean;
  disableWeekdays?: boolean;
  disabledDates?: Date[];
  disabledRanges?: { start: Date; end: Date }[];
  holidays?: Date[];
  disableDate?: (date: Date) => boolean;
}

/** Pull the disable-relevant props into a stable context object. */
export function disableContextFrom(props: DatePickerBaseProps, timezone: string, now: Date): DisableContext {
  return {
    timezone,
    now,
    minDate: props.minDate,
    maxDate: props.maxDate,
    disableFuture: props.disableFuture,
    disablePast: props.disablePast,
    disableWeekends: props.disableWeekends,
    disableWeekdays: props.disableWeekdays,
    disabledDates: props.disabledDates,
    disabledRanges: props.disabledRanges,
    holidays: props.holidays,
    disableDate: props.disableDate,
  };
}

/** Whether a calendar day should be non-selectable under the active constraints. */
export function isDayDisabled(day: Civil, ctx: DisableContext): boolean {
  const today = civilFromInstant(ctx.now, ctx.timezone);

  if (ctx.disableFuture && compareDate(day, today) > 0) return true;
  if (ctx.disablePast && compareDate(day, today) < 0) return true;

  if (ctx.minDate && compareDate(day, civilFromInstant(ctx.minDate, ctx.timezone)) < 0) return true;
  if (ctx.maxDate && compareDate(day, civilFromInstant(ctx.maxDate, ctx.timezone)) > 0) return true;

  if (ctx.disableWeekends) {
    const wd = weekday(day);
    if (wd === 0 || wd === 6) return true;
  }
  if (ctx.disableWeekdays) {
    const wd = weekday(day);
    if (wd >= 1 && wd <= 5) return true;
  }

  if (ctx.disabledDates?.some((d) => isSameDay(day, civilFromInstant(d, ctx.timezone)))) return true;
  if (ctx.holidays?.some((d) => isSameDay(day, civilFromInstant(d, ctx.timezone)))) return true;

  if (
    ctx.disabledRanges?.some((r) => {
      const s = civilFromInstant(r.start, ctx.timezone);
      const e = civilFromInstant(r.end, ctx.timezone);
      return compareDate(day, s) >= 0 && compareDate(day, e) <= 0;
    })
  ) {
    return true;
  }

  if (ctx.disableDate) {
    // Hand the callback a real instant (start of the civil day, best-effort UTC).
    const asDate = new Date(Date.UTC(day.year, day.month - 1, day.day));
    if (ctx.disableDate(asDate)) return true;
  }

  return false;
}

/** Whether a day is a holiday (for annotation, independent of disabling). */
/**
 * Month-granularity bounds for the `month` / `month-year` panels: a month is
 * disabled only when it lies entirely outside [minDate, maxDate] in the
 * picker's zone. Day-level rules (weekends, holidays, disabledDates) don't
 * apply — a month with any selectable day stays selectable.
 */
export function isMonthDisabled(year: number, month: number, ctx: DisableContext): boolean {
  const civilAt = (day: number): Civil => ({ year, month, day, hour: 0, minute: 0, second: 0 });
  if (ctx.minDate && compareDate(civilAt(daysInMonth(year, month)), civilFromInstant(ctx.minDate, ctx.timezone)) < 0) {
    return true;
  }
  if (ctx.maxDate && compareDate(civilAt(1), civilFromInstant(ctx.maxDate, ctx.timezone)) > 0) return true;
  if (ctx.disableFuture || ctx.disablePast) {
    const today = civilFromInstant(ctx.now, ctx.timezone);
    if (ctx.disableFuture && compareDate(civilAt(1), today) > 0) return true;
    if (ctx.disablePast && compareDate(civilAt(daysInMonth(year, month)), today) < 0) return true;
  }
  return false;
}

export function isHoliday(day: Civil, ctx: DisableContext): boolean {
  return ctx.holidays?.some((d) => isSameDay(day, civilFromInstant(d, ctx.timezone))) ?? false;
}
